import React, { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./VehicleDetails.css";
import { parseSpiderVOList } from "./vehicleEquipment";
import { getFinancingEligibility } from "./financingEligibility";

function SamplePrevArrow(props) {
  const { onClick } = props;

  return (
    <button
      className="rectangle-arrow-detail-left"
      onClick={onClick}
      aria-label="Précédent"
      type="button"
    >
      <svg width="14" height="14" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10 2L5 7.5L10 13" stroke="#111" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

function SampleNextArrow(props) {
  const { onClick } = props;

  return (
    <button
      className="rectangle-arrow-detail-right"
      onClick={onClick}
      aria-label="Suivant"
      type="button"
    >
      <svg width="14" height="14" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 2L10 7.5L5 13" stroke="#111" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

function getOwnerText(ownerCount) {
  const parsedCount = Number(ownerCount);

  if (!parsedCount || Number.isNaN(parsedCount)) {
    return null;
  }

  return `${parsedCount} main${parsedCount > 1 ? "s" : ""}`;
}

function VehicleDetails({ vehicle, onClose }) {
  const sliderRef = useRef(null);
  const fullscreenSliderRef = useRef(null);
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(82);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [fullscreenSlide, setFullscreenSlide] = useState(0);
  const [imageUrls, setImageUrls] = useState([]);
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    prevArrow: <SamplePrevArrow />,
    nextArrow: <SampleNextArrow />,
    adaptiveHeight: false,
    variableWidth: false,
    centerMode: false,
    waitForAnimate: false,
    afterChange: (index) => setCurrentSlide(index),
  };

  useEffect(() => {
    if (!vehicle) {
      setImageUrls([]);
      return;
    }

    if (Array.isArray(vehicle.images) && vehicle.images.length > 0) {
      setImageUrls(vehicle.images);
      return;
    }

    setImageUrls([process.env.PUBLIC_URL + "/vdispo.png"]);
  }, [vehicle]);

  useEffect(() => {
    setCurrentSlide(0);
    setFullscreenSlide(0);

    if (!imageUrls.length || !sliderRef.current) {
      return undefined;
    }

    const isDesktop = window.innerWidth > 768;
    const delay = isDesktop ? 300 : 200;

    const timer = setTimeout(() => {
      sliderRef.current?.slickGoTo(0);
      if (isDesktop) {
        window.dispatchEvent(new Event("resize"));
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [imageUrls]);

  useEffect(() => {
    const measureHeader = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.getBoundingClientRect().height + 8);
      }
    };

    const handleResize = () => {
      measureHeader();
      const current = sliderRef.current?.innerSlider?.state?.currentSlide ?? 0;

      if (!sliderRef.current) {
        return;
      }

      setTimeout(() => {
        sliderRef.current?.slickGoTo(current);
      }, 100);
    };

    measureHeader();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isFullscreenOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsFullscreenOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isFullscreenOpen]);

  useEffect(() => {
    if (!isFullscreenOpen || !fullscreenSliderRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      fullscreenSliderRef.current?.slickGoTo(fullscreenSlide, true);
      window.dispatchEvent(new Event("resize"));
    }, 40);

    return () => clearTimeout(timer);
  }, [fullscreenSlide, isFullscreenOpen]);

  if (!vehicle) {
    return null;
  }

  const cleanText = (value) => (typeof value === "string" ? value.trim() : "");

  const date = vehicle.dateCirculation ? new Date(vehicle.dateCirculation) : null;
  const isValidDate = date instanceof Date && !Number.isNaN(date.getTime());
  const monthNames = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ];

  const formattedDate = isValidDate
    ? `${monthNames[date.getMonth()]} ${date.getFullYear()}`
    : "Date non renseignée";

  const displayTitle = [vehicle.marque, vehicle.modele]
    .map((value) => cleanText(value))
    .filter(Boolean)
    .join(" ");

  const isSpiderSource = cleanText(vehicle.source).toLowerCase() === "spidervo";
  const spiderPreciseTitle = [
    cleanText(vehicle.spiderVersion),
    cleanText(vehicle.version),
  ].find(Boolean) || "";
  const fallbackPreciseTitle = [
    cleanText(vehicle.modelePrecis),
    cleanText(vehicle.modeleprecis),
    cleanText(vehicle.modele_precis),
    cleanText(vehicle["Modèle précis"]),
    cleanText(vehicle.finition),
    cleanText(vehicle.Finition),
  ].find(Boolean) || "";
  const preciseTitle = isSpiderSource ? spiderPreciseTitle : fallbackPreciseTitle;
  const priceValue = Number(vehicle.prix);
  const mileageValue = Number(vehicle.kilometrage);
  const ownerLabel = getOwnerText(vehicle.nbrmain);
  const locationText = [vehicle.localisation, vehicle.localisation_postale].filter(Boolean).join(", ");
  const financing = getFinancingEligibility(vehicle);

  // Calcul faible kilométrage (< 5 000 km/an en moyenne, max 100 000 km)
  const isFaibleKm = (() => {
    const km = Number(vehicle.kilometrage);
    const dateCirc = vehicle.dateCirculation ? new Date(vehicle.dateCirculation) : null;
    if (!dateCirc || !km || km >= 100000) return false;
    const ageAnnees = (new Date() - dateCirc) / (1000 * 60 * 60 * 24 * 365.25);
    if (ageAnnees < 0.5) return false;
    return (km / ageAnnees) <= 6500;
  })();

  // Garantie : nombre de mois du CRM, 12 par défaut
  const garantieMois = vehicle.garantieMois > 0 ? vehicle.garantieMois : 12;
  const garantieLabel = `${garantieMois} mois`;


  const equipmentItems = Array.from(new Set(parseSpiderVOList(vehicle.equipements || "")));
  const optionItems = Array.from(
    new Set(parseSpiderVOList(vehicle.options || "").filter((item) => !equipmentItems.includes(item)))
  );

  const stats = [
    { icon: "/calendar.svg", label: "Mise en circulation", value: formattedDate },
    {
      icon: "/kilometre.svg",
      label: "Kilométrage",
      value: Number.isFinite(mileageValue) ? `${mileageValue.toLocaleString("fr-FR")} km` : "Kilométrage non renseigné",
    },
    { icon: "/carburant.svg", label: "Carburant", value: vehicle.carburant || "Non renseigné" },
    { icon: "/boite.svg", label: "Boîte", value: vehicle.boiteVitesse || "Non renseignée" },
    ownerLabel ? { icon: "/owner.svg", label: "Propriétaires", value: ownerLabel } : null,
  ].filter(Boolean);

  const hasHistoriqueLink =
    vehicle.inspection && typeof vehicle.inspection === "string" && vehicle.inspection.startsWith("http");

  const openFullscreen = () => {
    setFullscreenSlide(currentSlide);
    setIsFullscreenOpen(true);
  };

  const closeFullscreen = () => {
    setIsFullscreenOpen(false);
  };

  return (
    <div className="ModalSup">
      <div className="detail-header" ref={headerRef}>
        <button className="bouton" onClick={onClose} type="button">
          ← Retour
        </button>
        <span className="detail-header-title">{[displayTitle, preciseTitle].filter(Boolean).join(" • ")}</span>
      </div>

      <div className="Modal">
        <div className="detail-media-column">
          <div className="detail-slider-sticky" style={{ top: headerHeight }}>
          <Slider ref={sliderRef} {...settings} className="DivSlider2">
            {imageUrls.length > 0 ? (
              imageUrls.map((url, index) => (
                <div key={`${url}-${index}`} className="slider-image-wrapper">
                  <button
                    className="detail-fullscreen-trigger"
                    type="button"
                    onClick={openFullscreen}
                    aria-label="Voir les photos en plein écran"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.667 6V2.667H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M9.999 2.667H13.332V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M13.332 10V13.333H9.999" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M6 13.333H2.667V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>Plein écran</span>
                  </button>
                  <img
                    className="slider-image2"
                    src={url}
                    alt={`${displayTitle || 'Véhicule'} - Vue ${index + 1}`}
                    onError={(event) => {
                      event.target.src = process.env.PUBLIC_URL + "/vdispo.png";
                    }}
                    onLoad={() => {
                      const current = sliderRef.current?.innerSlider?.state?.currentSlide ?? 0;
                      if (sliderRef.current) {
                        setTimeout(() => sliderRef.current?.slickGoTo(current), 50);
                      }
                    }}
                  />
                  <span className="photo-counter">{currentSlide + 1} / {imageUrls.length}</span>
                </div>
              ))
            ) : (
              <div className="slider-image-wrapper">
                <img className="slider-image2" src={process.env.PUBLIC_URL + "/vdispo.png"} alt="Visuel indisponible" />
              </div>
            )}
          </Slider>
          </div>
        </div>

        <div className="vehicle-info-detail-margin">
          <div className="vehicle-info">
            <section className="detail-overview-card">
              <div className="detail-title-wrap">
                <h1 className="vehicle-title">{displayTitle || "Véhicule disponible"}</h1>
                {preciseTitle ? <p className="vehicle-detailsPrecis">{preciseTitle}</p> : null}
                <div className="detail-price-hero">
                  <strong>{Number.isFinite(priceValue) ? `${priceValue.toLocaleString("fr-FR")} €` : "Sur demande"}</strong>
                  <span>TVA incluse</span>
                </div>
              </div>

              {(() => {
                const pointsForts = [
                  (
                    <div className="point-fort-detail-item" key="g">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11.5 14.5 16 10"/></svg>
                      <span className="point-fort-detail-label">Garantie</span>
                      <span className="point-fort-detail-value">{garantieLabel}</span>
                    </div>
                  ),
                  Number(vehicle.nbrmain) === 1 && (
                    <div className="point-fort-detail-item" key="pm">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="10" cy="7" r="4"/><path d="M17 11l2 2 4-4"/></svg>
                      <span className="point-fort-detail-label">Propriétaire</span>
                      <span className="point-fort-detail-value">1ère main</span>
                    </div>
                  ),
                  Number(vehicle.nbrmain) === 2 && vehicle.dateCirculation && ((new Date() - new Date(vehicle.dateCirculation)) / (1000 * 60 * 60 * 24 * 365.25)) > 6 && (
                    <div className="point-fort-detail-item" key="pm2">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="10" cy="7" r="4"/><path d="M17 11l2 2 4-4"/></svg>
                      <span className="point-fort-detail-label">Propriétaire</span>
                      <span className="point-fort-detail-value">2ème main</span>
                    </div>
                  ),
                  (!vehicle.vehiculeimporte || vehicle.vehiculeimporte === 'non') && (
                    <div className="point-fort-detail-item" key="fr">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                      <span className="point-fort-detail-label">Origine</span>
                      <span className="point-fort-detail-value">France</span>
                    </div>
                  ),
                  isFaibleKm && (
                    <div className="point-fort-detail-item" key="km">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      <span className="point-fort-detail-label">Kilométrage</span>
                      <span className="point-fort-detail-value">Faible</span>
                    </div>
                  ),
                ].filter(Boolean).slice(0, 4);
                if (pointsForts.length === 0) return null;
                return (
                  <div className="points-forts-detail">
                    <div className="points-forts-detail-grid" data-count={pointsForts.length}>
                      {pointsForts}
                    </div>
                  </div>
                );
              })()}

              {financing.credit && (
                <div className="detail-financing-panel">
                  <div className="detail-financing-icon" aria-hidden="true">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="5" width="20" height="14" rx="3" />
                      <path d="M2 10h20M6 15h4" />
                    </svg>
                  </div>
                  <div className="detail-financing-content">
                    <p className="detail-financing-eyebrow">Votre projet, votre formule</p>
                    <h2>Financement possible</h2>
                    <p className="detail-financing-intro">Découvrez les solutions envisageables pour ce véhicule.</p>
                    <div className="detail-financing-options">
                      {financing.credit && <span>Crédit classique</span>}
                      {financing.loa && <span>LOA</span>}
                      {financing.lld && <span>LLD</span>}
                    </div>
                    <p className="detail-financing-note">Sous réserve d’étude et d’acceptation de votre dossier.</p>
                  </div>
                </div>
              )}

              <div className="detail-meta-row">
                {locationText ? (
                  <p className="vehicle-localisation">
                    <img src="/location-black.svg" alt="Localisation" />
                    <span>{locationText}</span>
                  </p>
                ) : null}
                <div className="detail-meta-right">
                  {vehicle.livraison && (
                    <span className="detail-livraison-pill">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13" rx="2"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                      Livraison possible
                    </span>
                  )}
                  <span className="detail-availability-pill">Visite sur rendez-vous</span>
                </div>
              </div>
            </section>

            <section className="detail-section">
              <div className="detail-section-heading">
                <p className="detail-section-kicker">Vue rapide</p>
                <h2 className="detail-section-title">Les informations essentielles</h2>
              </div>
              <div className="detail-stats-grid">
                {stats.map((stat) => (
                  <div key={stat.label} className="detail-stat-chip">
                    <img src={stat.icon} alt={stat.label} />
                    <div className="detail-stat-content">
                      <small>{stat.label}</small>
                      <span>{stat.value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {(optionItems.length > 0 || equipmentItems.length > 0) && (
              <section className="detail-section">
                <div className="detail-section-heading">
                  <p className="detail-section-kicker">Configuration</p>
                  <h2 className="detail-section-title">Options et équipements</h2>
                </div>
                <div className="detail-feature-columns" key={vehicle.reference}>
                  {optionItems.length > 0 && (
                    <div className="vehicle-options-list">
                      <h3>Options</h3>
                      <ul>
                        {optionItems.slice(0, 6).map((item) => <li key={item}>{item}</li>)}
                      </ul>
                      {optionItems.length > 6 && (
                        <details className="detail-equipment-disclosure">
                          <summary>Voir les {optionItems.length - 6} autres options</summary>
                          <ul>{optionItems.slice(6).map((item) => <li key={item}>{item}</li>)}</ul>
                        </details>
                      )}
                    </div>
                  )}
                  {equipmentItems.length > 0 && (
                    <details className="vehicle-options-list detail-equipment-disclosure detail-equipment-full">
                      <summary>Tous les équipements <span>({equipmentItems.length})</span></summary>
                      <ul>{equipmentItems.map((item) => <li key={item}>{item}</li>)}</ul>
                    </details>
                  )}
                </div>
              </section>
            )}

            <section className="detail-section detail-contact-surface">
              <div className="detail-contact-top">
                <p className="detail-price-note">Contact rapide pour réservation, reprise ou financement.</p>
              </div>

              <div className="detail-cta-group">
                <a className="detail-cta detail-cta-primary" href="tel:+33682754629">
                  <img src="/phone-icon.svg" alt="Téléphone" />
                  Appeler
                </a>
                <a className="detail-cta detail-cta-secondary" href="sms:+33682754629">
                  <img src="/sms-icon.svg" alt="SMS" />
                  Envoyer un SMS
                </a>
              </div>

              <div className="detail-contact-block">
                <div className="detail-contact-row">
                  <div className="detail-contact-icon">
                    <img src="/mail-icon.svg" alt="Email" />
                  </div>
                  <div>
                    <strong>Email</strong>
                    <a href="mailto:contact@flipmycar.fr">contact@flipmycar.fr</a>
                  </div>
                </div>
                <div className="detail-contact-row">
                  <div className="detail-contact-icon">
                    <img src="/instagram.svg" alt="Instagram" />
                  </div>
                  <div>
                    <strong>Instagram</strong>
                    <a href="https://www.instagram.com/flipmycar.fr/" target="_blank" rel="noopener noreferrer">
                      @flipmycar.fr
                    </a>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      {isFullscreenOpen ? (
        <div className="detail-fullscreen-overlay" onClick={closeFullscreen} role="dialog" aria-modal="true">
          <button
            className="detail-fullscreen-close"
            type="button"
            onClick={closeFullscreen}
            aria-label="Fermer le plein écran"
          >
            Fermer
          </button>
          <div className="detail-fullscreen-content" onClick={(event) => event.stopPropagation()}>
            <Slider
              ref={fullscreenSliderRef}
              {...settings}
              className="detail-fullscreen-slider"
              afterChange={(index) => setFullscreenSlide(index)}
            >
              {imageUrls.map((url, index) => (
                <div key={`fullscreen-${url}-${index}`} className="detail-fullscreen-image-wrap">
                  <img
                    className="detail-fullscreen-image"
                    src={url}
                    alt={`Vue ${index + 1} en grand format`}
                    onError={(event) => {
                      event.target.src = process.env.PUBLIC_URL + "/vdispo.png";
                    }}
                  />
                </div>
              ))}
            </Slider>
            <span className="detail-fullscreen-counter">{fullscreenSlide + 1} / {imageUrls.length}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default VehicleDetails;
